import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  fetchFeaturedStoreProducts,
  fetchNewArrivalStoreProducts,
  fetchStoreProducts,
  fetchStoreProductsByIds,
} from '@/services/products'
import { subscribeBanners } from '@/services/banners'
import { subscribeCategories } from '@/services/categories'
import { fetchBestSellerProductIds } from '@/services/store/ordersApi'
import { useStoreGate } from '@/contexts/StoreGateContext'
import type { Product, Banner, Category } from '@/types'
import { HomeProductGrid } from '@/store/components/HomeProductGrid'
import { SectionHeading } from '@/store/components/SectionHeading'
import { HomePromoTicker } from '@/store/components/HomePromoTicker'
import { HomeBannerHero } from '@/store/components/HomeBannerHero'
import { RecommendationsSection } from '@/store/pages/RecommendationsSection'
import {
  HomeCategoriesSkeleton,
  HomeHeroSkeleton,
  HomeProductSectionSkeleton,
} from '@/store/components/StoreSkeletons'

export function HomePage() {
  const { settings } = useStoreGate()
  const showCategoriesOnHomepage = settings?.showCategoriesOnHomepage !== false

  const [featured, setFeatured] = useState<Product[]>([])
  const [bestSellers, setBestSellers] = useState<Product[]>([])
  const [newArrivals, setNewArrivals] = useState<Product[]>([])
  const [banners, setBanners] = useState<Banner[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [bannersReady, setBannersReady] = useState(false)
  const [featuredReady, setFeaturedReady] = useState(false)
  const [bestSellersReady, setBestSellersReady] = useState(false)
  const [newArrivalsReady, setNewArrivalsReady] = useState(false)
  const [categoriesReady, setCategoriesReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    const stopBanners = subscribeBanners(
      (items) => {
        if (cancelled) return
        setBanners(items.filter((x) => x.enabled))
        setBannersReady(true)
      },
      () => {
        if (!cancelled) setBannersReady(true)
      }
    )

    const stopCategories = subscribeCategories(
      (items) => {
        if (cancelled) return
        setCategories(items.filter((x) => x.active))
        setCategoriesReady(true)
      },
      () => {
        if (!cancelled) setCategoriesReady(true)
      }
    )

    let featuredLoaded: Product[] | null = null
    let newArrivalsLoaded: Product[] | null = null
    let bestSellersLoaded: Product[] | null = null
    let catalogFallbackStarted = false

    const maybeCatalogFallback = async () => {
      if (featuredLoaded === null || newArrivalsLoaded === null || bestSellersLoaded === null) {
        return
      }
      if (
        featuredLoaded.length > 0 ||
        newArrivalsLoaded.length > 0 ||
        bestSellersLoaded.length > 0
      ) {
        return
      }
      if (catalogFallbackStarted) return
      catalogFallbackStarted = true
      try {
        const catalog = await fetchStoreProducts()
        if (cancelled) return
        if (catalog.length > 0) {
          let nextFeatured = catalog.filter((p) => p.featured).slice(0, 4)
          if (nextFeatured.length === 0) {
            nextFeatured = catalog.slice(0, 4)
          }
          setFeatured(nextFeatured)
          setNewArrivals(catalog.slice(0, 4))
        }
      } catch {
        // Individual section fallbacks already attempted above.
      }
    }

    const loadBestSellers = async () => {
      try {
        const ids = await fetchBestSellerProductIds(4)
        if (cancelled) return
        let items: Product[] = []
        if (ids.length > 0) {
          try {
            items = await fetchStoreProductsByIds(ids)
          } catch {
            items = []
          }
        }
        if (cancelled) return
        bestSellersLoaded = items
        setBestSellers(items)
      } catch {
        if (!cancelled) bestSellersLoaded = []
      } finally {
        if (!cancelled) {
          setBestSellersReady(true)
          void maybeCatalogFallback()
        }
      }
    }

    void loadBestSellers()

    void fetchFeaturedStoreProducts(4)
      .then((items) => {
        if (cancelled) return
        featuredLoaded = items
        setFeatured(items)
        setFeaturedReady(true)
        void maybeCatalogFallback()
      })
      .catch(() => {
        if (cancelled) return
        featuredLoaded = []
        setFeaturedReady(true)
        void maybeCatalogFallback()
      })

    void fetchNewArrivalStoreProducts(4)
      .then((items) => {
        if (cancelled) return
        newArrivalsLoaded = items
        setNewArrivals(items)
        setNewArrivalsReady(true)
        void maybeCatalogFallback()
      })
      .catch(() => {
        if (cancelled) return
        newArrivalsLoaded = []
        setNewArrivalsReady(true)
        void maybeCatalogFallback()
      })

    return () => {
      cancelled = true
      stopBanners()
      stopCategories()
    }
  }, [])

  const allProductSectionsReady =
    featuredReady && bestSellersReady && newArrivalsReady

  const recommendationPool = useMemo(() => {
    const byId = new Map<string, Product>()
    for (const product of [...featured, ...bestSellers, ...newArrivals]) {
      byId.set(product.id, product)
    }
    return [...byId.values()]
  }, [featured, bestSellers, newArrivals])

  return (
    <div>
      <HomePromoTicker />
      {bannersReady ? <HomeBannerHero banners={banners} /> : <HomeHeroSkeleton />}

      {showCategoriesOnHomepage &&
        (categoriesReady ? (
          categories.length > 0 && (
            <section className="store-container py-16">
              <SectionHeading
                title="Categories"
                subtitle="Find your signature scent"
                href="/shop"
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {categories.slice(0, 8).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${encodeURIComponent(cat.name)}`}
                    className="glass-card gold-border-glow p-6 text-center hover:border-gold-400/40 transition-all"
                  >
                    <span className="font-display text-xl text-gold-300">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )
        ) : (
          <HomeCategoriesSkeleton />
        ))}

      {featuredReady ? (
        featured.length > 0 && (
          <section className="store-container py-8">
            <SectionHeading title="Featured Attars" href="/shop?featured=1" />
            <HomeProductGrid products={featured} />
          </section>
        )
      ) : (
        <HomeProductSectionSkeleton />
      )}

      {bestSellersReady ? (
        bestSellers.length > 0 && (
          <section className="store-container py-8">
            <SectionHeading title="Best Sellers" href="/shop" />
            <HomeProductGrid products={bestSellers} />
          </section>
        )
      ) : (
        <HomeProductSectionSkeleton />
      )}

      {newArrivalsReady ? (
        newArrivals.length > 0 && (
          <section className="store-container py-8">
            <SectionHeading
              title="New Arrivals"
              href="/shop?sort=new"
              linkLabel="See new"
            />
            <HomeProductGrid products={newArrivals} />
          </section>
        )
      ) : (
        <HomeProductSectionSkeleton />
      )}

      <section className="store-container py-16">
        <div className="glass-card gold-border-glow p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl font-bold gold-gradient-text">
              Our Story
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Bazm-e-Musk celebrates the heritage of oriental perfumery — pure attars
              composed with rare oud, musk, and floral essences. Each bottle reflects
              devotion to craft, luxury, and timeless elegance.
            </p>
          </div>
          <div className="rounded-2xl bg-luxury-800/50 p-8 border border-luxury-700">
            <p className="font-display text-2xl text-gold-300 italic text-center">
              &ldquo;Fragrance is memory worn on the skin.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {allProductSectionsReady && (
        <RecommendationsSection products={recommendationPool} />
      )}

      <section className="store-container pb-16">
        <div className="glass-card gold-border-glow p-8 text-center">
          <h3 className="font-display text-2xl text-gold-300">Follow Our Journey</h3>
          <p className="mt-2 text-sm text-zinc-500">Luxury attar inspiration on Instagram</p>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-4 text-sm text-gold-400 hover:underline"
          >
            @bazmemusk
          </a>
        </div>
      </section>
    </div>
  )
}
